import React, { useContext, memo, useEffect } from 'react'
import List from '@mui/material/List'
import WebOutlinedIcon from '@mui/icons-material/WebOutlined'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import { FaGlasses } from 'react-icons/fa'
import { FiUsers, FiCamera, FiSettings } from 'react-icons/fi'
import {
  MdOutlineConnectedTv,
  MdCloudQueue,
  MdLogout,
  MdOutlineAnalytics,
  MdRefresh,
  MdOutlineVpnKey,
  MdOutlineCalendarToday,
  MdVideoLibrary,
  MdDevices,
  MdOutlineFolder,
  MdOutlineHome,
  MdSmartToy
} from 'react-icons/md'
import { RiFlowChart, RiWifiLine, RiSignalWifiLine } from 'react-icons/ri'
import { HiOutlineChat } from 'react-icons/hi'
import SideBarRow from './SideBarRow.component'
import { CurrentPortalContext } from '../../../context/Shared/CurrentPortal.context'
import { CurrentUserContext } from '../../../context/Shared/CurrentUser.context'
import { ChatSocketContext } from '../../../context/ChatSocket/ChatSocket.context'
import PropTypes from 'prop-types'
import { theme } from '../../../styles/theme.style'
import { routify } from '../../../utils/functions'
import { Typography } from '@mui/material'
import { SideBarContext } from '../../../context/Shared/SideBar.context'
import styles from './SideBar.module.scss'

export const SideBarMenu = ({ signOut, navigateAway }) => {
  const { portal } = useContext(CurrentPortalContext)
  const { me } = useContext(CurrentUserContext)
  const { chats } = useContext(ChatSocketContext)
  const { isTabletOrMobile } = useContext(SideBarContext)
  const [unreadMessagesCount, setUnreadMessagesCount] = React.useState(0)

  // When an admin is simulating a different role, suppress the admin bypass
  // so the sidebar reflects the simulated role's permissions
  const isAdmin = me?.admin && portal?.role === 'Admin'

  useEffect(() => {
    let unreadMessagesCount = 0
    chats.forEach((chat) => (unreadMessagesCount += chat.unreadMessages))
    setUnreadMessagesCount(unreadMessagesCount)
  }, [chats])

  return (
    <List className="toolBarItems" sx={{ padding: 0, margin: 0, width: '100%' }}>
      {portal?.role !== 'Education' && (
        <>
          {/* TelePresence - Dark Blue Section */}
          <div className={styles.telePresenceSection}>
            <SideBarRow
              pageName="Home"
              activeKey={theme.HOMEVU}
              icon={<MdOutlineHome />}
              clickAction={() => navigateAway('/' + routify(theme.HOMEVU))}
            />
          </div>
          {/* Rest of items - Light Blue Section */}
          <div className={styles.menuSection}>
            {/* Users */}
            {(['Super User', 'Manager'].includes(portal?.role) || isAdmin) && (
              <SideBarRow
                pageName="Users"
                activeKey={theme.REMOTEVU}
                icon={<FiUsers />}
                clickAction={() => navigateAway('/' + routify(theme.REMOTEVU))}
              />
            )}
            {/* Devices */}
            {(['Super User', 'Manager'].includes(portal?.role) || isAdmin) && (
              <SideBarRow
                pageName="Devices"
                activeKey={theme.DEVICESVU}
                icon={<MdOutlineConnectedTv />}
                clickAction={() => navigateAway('/' + routify(theme.DEVICESVU))}
              />
            )}
            {/* Messaging */}
            {portal?.role !== 'Viewer' && (
              <SideBarRow
                pageName="Chat"
                activeKey={theme.CHATVU}
                icon={<HiOutlineChat />}
                badge={unreadMessagesCount}
                clickAction={() => navigateAway('/' + routify(theme.CHATVU))}
              />
            )}
            {/* CalendarVU */}
            {!['Education', 'Viewer'].includes(portal?.role) && (
              <SideBarRow
                pageName="Calendar"
                activeKey={theme.CALENDARVU}
                icon={<MdOutlineCalendarToday />}
                clickAction={() => navigateAway('/' + routify(theme.CALENDARVU))}
              />
            )}
            {/* Flows */}
            {portal?.role !== 'Viewer' && portal?.hasFlowVULicence && (
              <SideBarRow
                pageName="Workflows"
                activeKey={theme.FLOWVU}
                icon={<RiFlowChart />}
                clickAction={() => navigateAway('/' + routify(theme.FLOWVU))}
              />
            )}
            {/* Library - hidden for now */}
            {/* <SideBarRow
              pageName="Library"
              activeKey={theme.LIBRARYVU}
              icon={<MdVideoLibrary />}
              clickAction={() => navigateAway('/' + routify(theme.LIBRARYVU))}
            /> */}
            {/* Media */}
            {(['User', 'Super User', 'Manager', 'Education'].includes(portal?.role) ||
              isAdmin) && (
              <SideBarRow
                pageName="Files"
                activeKey={theme.FILEVU}
                icon={<MdOutlineFolder />}
                clickAction={() => navigateAway('/' + routify(theme.FILEVU))}
              />
            )}
            {/* AgentVU */}
            {isAdmin && (
              <SideBarRow
                pageName="Agents"
                activeKey={theme.AGENTVU}
                icon={<MdSmartToy />}
                clickAction={() => navigateAway('/' + routify(theme.AGENTVU))}
              />
            )}
            {/* PatientVU */}
            {(['Super User', 'Manager', 'User'].includes(portal?.role) || isAdmin) && (
              <SideBarRow
                pageName="Patients"
                activeKey={theme.PATIENTVU}
                icon={<LocalHospitalOutlinedIcon />}
                clickAction={() => navigateAway('/' + routify(theme.PATIENTVU))}
              />
            )}
            {/* Reports */}
            {(['Super User', 'Manager'].includes(portal?.role) || isAdmin) && (
              <SideBarRow
                pageName="Reports "
                activeKey={theme.REPORTVU}
                icon={<MdOutlineAnalytics />}
                clickAction={() => navigateAway('/' + routify(theme.REPORTVU))}
              />
            )}
            {/* LicenceVU */}
            {(['Super User', 'Manager'].includes(portal?.role) || isAdmin) && (
              <SideBarRow
                pageName="Licences"
                activeKey={theme.LICENCEVU}
                icon={<MdOutlineVpnKey />}
                clickAction={() => navigateAway('/' + routify(theme.LICENCEVU))}
              />
            )}
            {/* Portals */}
            {isAdmin && (
              <SideBarRow
                pageName="Portals"
                activeKey={theme.PORTALVU}
                icon={<WebOutlinedIcon />}
                clickAction={() => navigateAway('/' + routify(theme.PORTALVU))}
              />
            )}
            {isTabletOrMobile && (
              <>
                <SideBarRow
                  pageName="Settings"
                  activeKey={theme.SETTINGS}
                  icon={<FiSettings />}
                  clickAction={() => navigateAway('/' + routify(theme.SETTINGS))}
                />
                <SideBarRow
                  pageName="Refresh"
                  icon={<MdRefresh />}
                  clickAction={() => window.location.reload()}
                />
                <SideBarRow
                  pageName="Logout"
                  icon={<MdLogout />}
                  clickAction={signOut}
                />
              </>
            )}
          </div>
        </>
      )}
    </List>
  )
}

SideBarMenu.propTypes = {
  navigateAway: PropTypes.func,
  signOut: PropTypes.func
}

export const MemoizedSideBarMenu = memo(SideBarMenu)
